import type { TypographyName } from '@zapier/design-system';
import type { DocumentNode } from 'graphql';
import {
  PaymentSourceType,
  paymentMethodQuery_account_paymentSources as PaymentMethodQueryPaymentSources,
  paymentMethodQuery_account_paymentSources_details as PaymentMethodQueryPaymentSourcesDetails,
  paymentMethodQuery_account_paymentSources_details_PaymentSourceCard as PaymentMethodQueryPaymentSourceCard,
  paymentMethodQuery_account_paymentSources_details_PaymentSourceBank as PaymentMethodQueryPaymentSourceBank,
  paymentMethodQuery_account_paymentSources_details_PaymentSourcePaypal as PaymentMethodQueryPaymentSourcePayPal,
} from './graphql-types';

type TPaymentSource<
  K extends PaymentSourceType,
  D extends PaymentMethodQueryPaymentSourcesDetails
> = PaymentMethodQueryPaymentSources & {
  kind: K;
  details: D | null;
};

export type CreditCardPaymentSource = TPaymentSource<
  PaymentSourceType.card,
  PaymentMethodQueryPaymentSourceCard
>;

export type BankPaymentSource = TPaymentSource<
  PaymentSourceType.bank,
  PaymentMethodQueryPaymentSourceBank
>;

export type PayPalPaymentSource = TPaymentSource<
  PaymentSourceType.paypal,
  PaymentMethodQueryPaymentSourcePayPal
>;

export type PaymentSource =
  | CreditCardPaymentSource
  | BankPaymentSource
  | PayPalPaymentSource;

export * from './graphql-types';

// Loosely typed types to support dynamic `useQuery`.
export type OperationVariables = Record<string, any>;

export type QueryResult<Data> = {
  client: any;
  data: Data;
  error?: any;
  loading: boolean;
  networkStatus: any;
  called: boolean;
};

type TData = any;

type UseQuery<TData = any> = (
  query: DocumentNode,
  opts: {}
) => QueryResult<TData>;

type MutationOptions = {};

type FetchResult<TData = {}> = {
  data?: TData | null;
  extensions?: any;
  context?: any;
};

type MutationResult<TData> = Partial<QueryResult<TData>>;

type MutateFunction<TData> = (
  opts: MutationOptions
) => Promise<FetchResult<TData>>;

type UseMutation<TData = any> = (
  query: DocumentNode,
  opts: MutationOptions
) => [MutateFunction<TData>, MutationResult<TData>];

export type ApolloInjection = {
  useMutation?: UseMutation<TData>;
  useQuery?: UseQuery<TData>;
};

export type State = {
  fields: Fields;
  hasErrors: boolean;
  hasSubmitted: boolean;
  isDirty: boolean;
  isTokenizing: boolean;
  isValid: boolean;
  validators: object;
};

export type HostedFieldAdditionalData = {
  addressCountry: string;
  addressState: string;
  addressCity: string;
  addressZip: string;
  name: string;
};

export type FieldName = string;
export type FieldValue = string | SensitiveValue;

export type PaymentDetailsPaypal = TPaymentDetails<PaymentDetailsPaypalFields>;

export type PaymentDetailsPaypalFields = {
  email: string;
};

export type PaymentDetailsAddress = {
  addressCountry: string;
  addressState: string;
  addressZip: string;
};

export type TPaymentDetails<TFields> = PaymentDetailsAddress & TFields;

export type PaymentDetailsBankFields = {
  accountHolderName: string;
  accountType: string;
  addressCity: string | null | undefined;
  addressCountry: string | null | undefined;
  addressState: string | null | undefined;
  addressZip: string | null | undefined;
  paymentProcessor: string;
  bankName: string;
  last4: string;
  ownershipType: string;
  routingNumber: string;
  verified: true;
};

export type PaymentDetailsBank = TPaymentDetails<PaymentDetailsBankFields>;

export type FieldError = {
  isFixed: boolean;
  message: string;
};

export type TFieldState<TValue = string> = {
  error: FieldError | null | undefined;
  isSensitive: boolean;
  prettyName: string;
  value: TValue;
};

export type NormalFieldState = TFieldState;

export type BankFormFields = {
  accountNumber: NormalFieldState;
  accountType: NormalFieldState;
  businessName: NormalFieldState;
  extendedAddress: NormalFieldState;
  firstName: NormalFieldState;
  lastName: NormalFieldState;
  locality: NormalFieldState;
  ownershipType: NormalFieldState;
  postalCode: NormalFieldState;
  region: NormalFieldState;
  routingNumber: NormalFieldState;
  streetAddress: NormalFieldState;
  isSubmethodSupported: NormalFieldState;
  currency: NormalFieldState;
};

export type CreditCardFormFields = {
  addressCountry: NormalFieldState;
  addressState: NormalFieldState;
  addressZip: NormalFieldState;
  cvc: SensitiveFieldState;
  expiry: SensitiveFieldState;
  name: NormalFieldState;
  number: SensitiveFieldState;
  isSubmethodSupported: NormalFieldState;
  currency: NormalFieldState;
};

export type CreditCardBrandCode =
  | 'american-express'
  | 'diners-club'
  | 'discover'
  | 'jcb'
  | 'mastercard'
  | 'master-card'
  | 'unionpay'
  | 'visa'
  | 'unknown';

export type HostedFieldValue = {
  brand?: CreditCardBrandCode | null | undefined;
  isEmpty: boolean;
  originalError: string;
};

export type SensitiveValue = HostedFieldValue;

export type Fields = CreditCardFormFields | BankFormFields;
export type SensitiveFieldState = TFieldState<SensitiveValue>;
export type FieldState = NormalFieldState | SensitiveFieldState;

export type CreditCardBrand =
  | 'American Express'
  | 'Diners Club'
  | 'Discover'
  | 'JCB'
  | 'MasterCard'
  | 'UnionPay'
  | 'Unknown'
  | 'Visa';

export type ApiKeys = { [key: string]: string };

export type CreditCardDefinition = {
  code: {
    name: string;
    size: number;
  };
  gaps: number[];
  lengths: number[];
  niceType: string;
  type: string;
};

export type CreditCardFields = {
  definition: CreditCardDefinition;
  expiry: string;
  last4: string;
};

export type HostedFieldType = 'number' | 'expiry' | 'cvc';

export type HostedFieldToken = {
  details: {
    address_country: string;
    address_state: string;
    address_zip: string;
    address_city: string | undefined | null;
    brand: string;
    exp_month: string;
    exp_year: string;
    last4: string;
  };
  token: string;
};

export interface HostedFieldProcessorInterface {
  createFields(
    style: any,
    onChange: (type: HostedFieldType, value: HostedFieldValue) => void
  ): Promise<void>;

  destroyFields(): void;

  createToken(
    additionalData: HostedFieldAdditionalData
  ): Promise<HostedFieldToken>;
}

export type Validator<TState, TFields = Fields> = (
  fieldState: TState,
  fields: TFields
) => string;

export type Validators<TFields> = {
  [P in keyof TFields]: Validator<TFields[P], TFields>;
};

export type SupportedPaymentMethod = {
  method: PaymentSourceType;
  paymentProcessor: PaymentProcessor | undefined;
  submethod: string;
};

export type PaymentDetailsCardFields = CreditCardFields & {
  paymentProcessor: PaymentProcessor;
  brand: string;
  name: string;
};

export type PaymentDetailsCard = TPaymentDetails<PaymentDetailsCardFields>;

export type PaymentSourceDetails =
  | PaymentDetailsCard
  | PaymentDetailsPaypal
  | PaymentDetailsBank;

export type PaymentSourceInput = {
  method: PaymentSourceType;
  details: PaymentSourceDetails;
  paymentProcessor: PaymentProcessor;
  token: string;
};

export interface PaymentSourceHandler {
  save(details: object): Promise<object | null | undefined>;
  select(data: object): PaymentSourceDetails;
}

export type InvoiceStatus = 'refunded' | 'paid' | 'scheduled' | 'error';

export type InvoiceStatusFlags = {
  isPaid: boolean | null | undefined;
  isRefunded: boolean | null | undefined;
  isUpcoming: boolean | null | undefined;
};

export type Invoice = InvoiceStatusFlags &
  ApolloInjection & {
    amountDue: number;
    createdAt: string;
    currency: string;
    discount:
      | {
          coupon:
            | {
                amountOff: number;
                percentOff: number;
              }
            | null
            | undefined;
        }
      | null
      | undefined;
    payment:
      | {
          source:
            | {
                details: PaymentSourceDetails | null | undefined;
                method: PaymentSourceType | null;
              }
            | null
            | undefined;
        }
      | null
      | undefined;
    printableUrl: string | null | undefined;
    reason: string;
    showInvoiceColumn: boolean;
    status: InvoiceStatus;
  };

export type InvoiceSection = Invoice & {
  direction?: string;
  isSmall?: boolean;
  status: string;
  textType: TypographyName;
};

export enum PaymentProcessor {
  stripe = 'stripe',
  braintree = 'braintree',
}
